"use client";
import ImageWithSkeleton from "@/components/ui/ImageWithSkeleton";
import MarkdownRenderer from "@/components/ui/MarkdownRenderer";
import { useAppContext } from "@/providers/AppProvider";
import { ICaseStudy, IFile, IMetric } from "@/types";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Moon, Sun, Tag } from "lucide-react";
import Link from "next/link";

interface CaseStudyClientDetailProps {
    caseStudy: ICaseStudy;
}

export default function CaseStudyClientDetail({
    caseStudy,
}: CaseStudyClientDetailProps) {
    const { theme, toggleTheme } = useAppContext();

    const coverUrl = (caseStudy?.coverImage as IFile)?.cloudinaryUrl || "";

    const words = (caseStudy?.content || "").split(/\s+/).length;
    const readTime = Math.max(1, Math.ceil(words / 200));

    return (
        <div className="min-h-screen bg-background-color pb-20 text-text-color">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-border-color bg-card-bg px-6 py-3.5">
                <div className="mx-auto flex max-w-4xl items-center justify-between">
                    <Link
                        href="/case-studies"
                        className="flex items-center gap-1.5 rounded-xl border border-border-color bg-card-bg px-3.5 py-1.5 text-xs font-semibold text-text-color transition hover:border-primary hover:text-primary"
                    >
                        <ArrowLeft className="h-4 w-4" /> All Case Studies
                    </Link>
                    <span className="text-xs font-bold text-primary sm:text-sm">
                        Sahil Atahar
                    </span>
                    <button
                        onClick={toggleTheme}
                        className="flex items-center justify-center rounded-xl border border-border-color bg-card-bg p-2 text-text-color outline-none transition hover:border-primary hover:text-primary"
                        aria-label="Toggle Theme"
                    >
                        {theme === "light" ? (
                            <Moon className="h-4 w-4" />
                        ) : (
                            <Sun className="h-4 w-4" />
                        )}
                    </button>
                </div>
            </header>

            {/* Article Container - Max fixed width centered, clean plane layout */}
            <motion.article
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mx-auto max-w-4xl space-y-8 px-4 py-10"
            >
                {/* Header Information */}
                <div className="space-y-4 border-b border-border-color pb-8">
                    <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                        <span className="flex items-center gap-1 font-semibold text-primary">
                            <Clock className="h-3.5 w-3.5" /> {readTime} min
                            read
                        </span>
                        {caseStudy.tags?.map((tag: string) => (
                            <span
                                key={tag}
                                className="flex items-center gap-1 font-medium text-primary/80"
                            >
                                <Tag className="h-3.5 w-3.5" /> #{tag}
                            </span>
                        ))}
                    </div>
                    <h1 className="text-3xl font-black leading-tight text-primary sm:text-4xl">
                        {caseStudy.title}
                    </h1>
                    <p className="text-text-color/90 rounded-r-xl border-l-4 border-primary bg-primary/5 py-1 pl-4 text-base italic leading-relaxed">
                        {caseStudy.summary}
                    </p>
                </div>
                {/* Cover Image */}
                {coverUrl && (
                    <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-border-color shadow-lg">
                        <ImageWithSkeleton
                            src={coverUrl}
                            alt={caseStudy.title}
                            isLoadingUrl={false}
                            fill
                            sizes="(max-width: 1024px) 100vw, 900px"
                            containerClassName="relative h-full w-full"
                            className="object-cover"
                        />
                    </div>
                )}

                {/* Article Content - Clean Plane Article Layout */}
                <div className="pt-4">
                    <MarkdownRenderer
                        content={caseStudy.content}
                        images={caseStudy.images as IFile[]}
                    />
                </div>

                {/* Key Impact Metrics Banner (Bottom) */}
                {caseStudy.metrics && caseStudy.metrics.length > 0 && (
                    <div className="mt-6 rounded-2xl border border-primary bg-primary/10 p-3.5 shadow-sm sm:p-5">
                        <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-primary sm:text-xs">
                            ⚡ Key Results & Impact Metrics
                        </h3>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3.5">
                            {caseStudy.metrics.map(
                                (m: IMetric, idx: number) => (
                                    <div
                                        key={idx}
                                        className="rounded-xl border border-border-color bg-card-bg p-2.5 text-center sm:p-3"
                                    >
                                        <p className="text-sm font-extrabold text-primary sm:text-lg">
                                            {m.value}
                                        </p>
                                        <p className="text-muted-foreground mt-0.5 text-[10px] font-medium leading-tight sm:text-xs">
                                            {m.label}
                                        </p>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                )}
            </motion.article>
        </div>
    );
}
